import { useState, useCallback, useMemo, useEffect } from "react";
import { LandingScreen } from "./components/LandingScreen";
import { PredictorScreen } from "./components/PredictorScreen";
import { LeaderboardScreen } from "./components/LeaderboardScreen";
import { AdminScreen } from "./components/AdminScreen";
import { AboutPage } from "./components/AboutPage";
import { FAQPage } from "./components/FAQPage";
import { PrivacyPage } from "./components/PrivacyPage";
// import { FullScreenAd } from "./components/FullScreenAd";
import { SEO } from "./components/SEO";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CountryTranslationsProvider } from "./contexts/CountryTranslationsContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SquadProvider } from "./contexts/SquadContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Preloader } from "./components/Preloader";
import { SquadJoinHandler } from "./components/SquadJoinHandler";
import { LoginModal } from "./components/LoginModal";
import { CreateSquadModal } from "./components/CreateSquadModal";
import { JoinSquadModal } from "./components/JoinSquadModal";
import { FirstTimePredictionModal } from "./components/FirstTimePredictionModal";
import { PendingPredictionHandler } from "./components/PendingPredictionHandler";
import { PodiumModalHandler } from "./components/PodiumModalHandler";
import { ModalProvider } from "./contexts/ModalContext";
import { ToastProvider } from "./contexts/ToastContext";
import { useMatches } from "./hooks/useMatches";
import type { Screen, Match } from "./types";

// Component to handle first-time modal logic (needs to be inside providers)
function FirstTimeModalHandler() {
  const { user, loading: authLoading } = useAuth();
  const { matches, loading: matchesLoading } = useMatches();
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);

  const handleCloseModal = useCallback(() => {
    setShowFirstTimeModal(false);
  }, []);

  useEffect(() => {
    // Only show if:
    // 1. Auth is not loading
    // 2. User is not logged in
    // 3. Matches are loaded
    // 4. There's at least one upcoming match
    // Note: We don't check FIRST_TIME_MODAL_SHOWN_KEY here - modal should show again if user closes it
    // It will only be hidden permanently after they sign up
    if (
      !authLoading &&
      !user &&
      !matchesLoading &&
      matches.some((m) => m.status === "upcoming")
    ) {
      // Small delay to ensure smooth UI
      const timer = setTimeout(() => {
        setShowFirstTimeModal(true);
      }, 500);
      return () => clearTimeout(timer);
    } else if (user) {
      // If user is logged in, hide the modal
      setShowFirstTimeModal(false);
    }
  }, [authLoading, user, matchesLoading, matches]);

  return (
    <FirstTimePredictionModal
      isOpen={showFirstTimeModal}
      onClose={handleCloseModal}
    />
  );
}

export default function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>("landing");
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  // const [showFullScreenAd, setShowFullScreenAd] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);
  const [showJoinSquadModal, setShowJoinSquadModal] = useState(false);

  // Track last ad shown time (1 minute cooldown)
  // const lastAdShownRef = useRef<number>(0);
  // const previousScreenRef = useRef<Screen>("landing");

  const handleMatchClick = useCallback((match: Match) => {
    setSelectedMatch(match);
    setCurrentScreen("predictor");
  }, []);

  const handleNavigate = useCallback((screen: Screen) => {
    setCurrentScreen(screen);
  }, []);

  const handleBack = useCallback(() => {
    setCurrentScreen("landing");
  }, []);

  // Scroll to top when screen changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [currentScreen]);

  // const handleCloseAd = useCallback(() => {
  //   setShowFullScreenAd(false);
  //   lastAdShownRef.current = Date.now();
  // }, []);

  // Show full-screen ad with 1 minute cooldown
  // const showAd = useCallback(() => {
  //   const now = Date.now();
  //   const timeSinceLastAd = now - lastAdShownRef.current;
  //   const cooldown = 60 * 1000; // 1 minute

  //   // Check cooldown
  //   if (timeSinceLastAd < cooldown) {
  //     return;
  //   }

  //   // Don't show if already showing
  //   if (showFullScreenAd) {
  //     return;
  //   }

  //   setShowFullScreenAd(true);
  //   lastAdShownRef.current = now;
  // }, [showFullScreenAd]);

  // Show ad on screen navigation (except initial load)
  // useEffect(() => {
  //   if (
  //     previousScreenRef.current !== currentScreen &&
  //     previousScreenRef.current !== "landing"
  //   ) {
  //     const timeoutId = setTimeout(() => {
  //       showAd();
  //     }, 500);

  //     previousScreenRef.current = currentScreen;

  //     return () => {
  //       clearTimeout(timeoutId);
  //     };
  //   } else {
  //     previousScreenRef.current = currentScreen;
  //   }
  // }, [currentScreen, showAd]);

  // Show ad when viewing leaderboard
  // useEffect(() => {
  //   if (currentScreen === "leaderboard") {
  //     const timeoutId = setTimeout(() => {
  //       showAd();
  //     }, 1000);

  //     return () => {
  //       clearTimeout(timeoutId);
  //     };
  //   }
  // }, [currentScreen, showAd]);

  const screenContent = useMemo(() => {
    switch (currentScreen) {
      case "landing":
        return (
          <LandingScreen
            onMatchClick={handleMatchClick}
            onNavigate={handleNavigate}
          />
        );
      case "predictor":
        return selectedMatch ? (
          <PredictorScreen
            match={selectedMatch}
            onBack={handleBack}
            onNavigate={handleNavigate}
            // onShowAd={showAd}
          />
        ) : null;
      case "leaderboard":
        return (
          <LeaderboardScreen onBack={handleBack} onNavigate={handleNavigate} />
        );
      case "admin":
        return <AdminScreen onNavigate={handleNavigate} />;
      case "about":
        return <AboutPage onBack={handleBack} onNavigate={handleNavigate} />;
      case "faq":
        return <FAQPage onBack={handleBack} onNavigate={handleNavigate} />;
      case "privacy":
        return <PrivacyPage onBack={handleBack} onNavigate={handleNavigate} />;
      default:
        return null;
    }
  }, [
    currentScreen,
    selectedMatch,
    handleMatchClick,
    handleNavigate,
    handleBack,
    // showAd,
  ]);

  // Get match info for SEO
  const matchInfo = useMemo(() => {
    if (selectedMatch && currentScreen === "predictor") {
      return {
        homeTeam: selectedMatch.homeTeam || "",
        awayTeam: selectedMatch.awayTeam || "",
        matchTime: selectedMatch.matchTime || "",
      };
    }
    return undefined;
  }, [selectedMatch, currentScreen]);

  return (
    <ErrorBoundary>
      <SEO screen={currentScreen} match={matchInfo} />
      <LanguageProvider>
        <AuthProvider>
          <SquadProvider>
            <Preloader>
              <CountryTranslationsProvider>
                <ToastProvider>
                  <ModalProvider
                    openLoginModal={() => setShowLoginModal(true)}
                    openCreateSquadModal={() => setShowCreateSquadModal(true)}
                    openJoinSquadModal={() => setShowJoinSquadModal(true)}
                  >
                    <div className="min-h-screen bg-slate-50 gradient-mesh">
                      <SquadJoinHandler />
                      <PendingPredictionHandler />
                      <FirstTimeModalHandler />
                      <PodiumModalHandler />
                      {screenContent}

                      {/* <FullScreenAd
                        isOpen={showFullScreenAd}
                        onClose={handleCloseAd}
                        showCloseDelay={5}
                      /> */}

                      {/* Header Modals */}
                      <LoginModal
                        isOpen={showLoginModal}
                        onClose={() => setShowLoginModal(false)}
                      />
                      <CreateSquadModal
                        isOpen={showCreateSquadModal}
                        onClose={() => setShowCreateSquadModal(false)}
                        onSuccess={() => {
                          setShowCreateSquadModal(false);
                        }}
                      />
                      <JoinSquadModal
                        isOpen={showJoinSquadModal}
                        onClose={() => setShowJoinSquadModal(false)}
                      />
                    </div>
                  </ModalProvider>
                </ToastProvider>
              </CountryTranslationsProvider>
            </Preloader>
          </SquadProvider>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
